﻿import { registerCard } from '../components/card';
import { createElement, formData, querystring, loadIcon } from '../utils';

enum SearchHitType {
    Document = 'Document',
    Link = 'Link',
}

interface SearchHit {
    title: string | null;
    url: string | null;
    excerpt: string | null;
    type: SearchHitType;
}

interface SearchResponse {
    searchInFiles: boolean;
    pageSize: number;
    page: number;
    hasNext: boolean;
    hits: SearchHit[] | null;
    totalHits: number;
}

interface SearchRequest {
    query: string;
    page: number;
    searchInFiles: boolean | null;
}

/*
 * Helper function for toggling hidden class
 */
export function registerSearchPage(): void {
    const FORM_SELECTOR = '#searchForm';
    const HITS_SELECTOR = '#searchResults';
    const INFO_SELECTOR = '#searchInfo';
    const NEXT_SELECTOR = '#showMoreResults';
    const SPINNER_SELECTOR = '#searchSpinner';
    const SEARCH_ENDPOINT = '/api/search';
    const DOCUMENT_ICON_ID = 'iconDocument';
    const LINK_ICON_ID = 'iconLink';

    const $info = $(INFO_SELECTOR);
    const $hits = $(HITS_SELECTOR);
    const $next = $(NEXT_SELECTOR);
    const $form = $(FORM_SELECTOR);
    const $spinner = $(SPINNER_SELECTOR);

    // Keep track of last reuqests state
    let state: SearchRequest = {
        query: '',
        page: 1,
        searchInFiles: false,
    };

    function createHit(hit: SearchHit): Element {
        let $icon: SVGElement | null = null;

        if (hit.type == SearchHitType.Document) {
            $icon = loadIcon(DOCUMENT_ICON_ID);
        } else {
            $icon = loadIcon(LINK_ICON_ID)
        }

        const $hit = createElement('div', 'group flex flex-col mb-5 cursor-pointer focus:outline-none focus-visible:outline-secondary',
            createElement('div', 'inline-flex',
                $icon,
                createElement('span', 'flex-1 leading-snug font-medium underline lg:no-underline group-hover:underline', hit.title),
            ),
            hit.excerpt && createElement('p', 'ml-8 leading-snug', hit.excerpt),
        );

        registerCard($hit, hit.url ?? '#', hit.title);

        return $hit;
    }

    function postSearch(request: SearchRequest): void {
        if (request.page == 1) {
            $hits.empty();
            $info.empty();

            if (!request.query) {
                const $strong = createElement('strong', 'bold', 'Sök');

                $info.append('Inget sökresultat visas eftersom sökrutan inte var ifylld. Fyll i sökrutan och klicka på knappen ”', $strong, '”.');
                $next.addClass('hidden');
                return;
            }
        }

        // Hide next button, show spinner
        $next.addClass('hidden');
        $spinner.removeClass('hidden');

        $.getJSON(`${SEARCH_ENDPOINT}?${querystring(request)}`)
            .done((res: SearchResponse) => {
                if (Array.isArray(res.hits)) {
                    res.hits.forEach((hit) => {
                        $hits.append(createHit(hit));
                    });
                }

                // Show/hide next button
                $next.toggleClass('hidden', !res.hasNext);

                // Set info text on first fetch
                if (res.page == 1) {
                    const $strong = createElement('strong', 'bold', request.query);

                    $info.append('Din sökning på ”', $strong, `” gav ${res.totalHits ?? 0} träffar`);
                }

                request.page += 1;
            })
            .fail(() => {
                console.log('Request failed');
            })
            .always(() => {
                $spinner.addClass('hidden');
                state = request;
            });
    }

    function postForm(): void {
        const { query, searchInFiles } = formData($form);

        postSearch({
            query,
            page: 1,
            searchInFiles: searchInFiles == 'true',
        });
    }

    if ($form.length) {
        $next.on('click', () => postSearch(state));

        // Initialize
        $form.on('submit', (e: JQuery.SubmitEvent) => {
            e.preventDefault();

            postForm();
        });

        // Do initial search
        postForm();
    }
}
